import React, { Dispatch, FC } from 'react'
import { Button, Form } from 'react-bootstrap'
import capitalize from 'lodash.capitalize'

import { Annotation, AnnotationData, AutocompleteableItem } from 'types'
import { Field } from 'models/Field'
import { SignatorySelect } from '../SignatorySelect'
import { useForm } from 'hooks/useForm'
import { useProject } from 'hooks/useProject'

export interface AnnotationEditorProps {
  annotation: Annotation
  onChange: Dispatch<React.SetStateAction<Annotation | undefined>>
  onSubmit: () => void
  mode?: 'create' | 'update'
  onDelete?: (annotation: Annotation) => void
  currentPage: number
  projectId: number
}

type OnSubmitCallbackParams = {
  annotation: Annotation
  annotationId: number
  currentPage: number
  defaultValues: DefaultFields
  onChange: Dispatch<React.SetStateAction<Annotation | undefined>>
  onSubmit: () => void
}

type DefaultFields = {
  text: string
  inputType: string
}

const defaultFieldValues = { text: '', inputType: Field.fieldTypes.TEXT }

// Pseudorandom ID must be provided by this component
const generatePseudoRandomID = (xCoordinate: number) =>
  new Date().getUTCMilliseconds() * Math.floor(xCoordinate)

export const onSubmitCallback = ({
  annotation,
  annotationId,
  currentPage,
  defaultValues,
  onChange,
  onSubmit,
}: OnSubmitCallbackParams) => (formData: Partial<AnnotationData>) => {
  const pageNumber = currentPage

  const previousData = annotation.data
  const data = {
    ...defaultValues,
    ...previousData,
    ...formData,
    id: annotationId,
    pageNumber,
  }
  if (annotation.data && annotation.data.id) {
    data.id = annotation.data.id
  }

  onChange({ ...annotation, data })
  // The react-image-annotation library has a really unfortunate interface.
  // 'onSubmit' function reflects props passed into that library's component, `Annotation`
  // 'onSubmit' takes the `Annotation.onSubmit` and calls it with the `Annotation.value`.
  // `onChange` updates the value passed into `Annotation.value`.
  // This means that we need to wait a render before `Annotation.value` reflects this data.
  // `setTimeout` puts the `onSubmit` call into the render stack,
  // so it will get called after `Annotation.value` gets reflected properly.
  // I do not love this.

  // - Alex Jarvis, 11/26/19
  setTimeout(() => onSubmit())
}

export const AnnotationEditor: FC<AnnotationEditorProps> = ({
  annotation,
  onChange,
  onSubmit,
  mode,
  onDelete,
  currentPage,
  projectId,
}) => {
  const [
    inputs,
    changeHandler,
    submitHandler,
    validated,
    includeFormManagedValue,
  ] = useForm<AnnotationData>(
    onSubmitCallback({
      annotation,
      annotationId: generatePseudoRandomID(annotation.geometry.x),
      currentPage,
      defaultValues: defaultFieldValues,
      onChange,
      onSubmit,
    }),
  )

  const [, , deleteHandler] = useForm<AnnotationData>(
    formData => onDelete && onDelete({ ...annotation }),
  )

  const fieldTypeOptions = Object.values(Field.fieldTypes).map(fieldType => (
    <option key={fieldType} value={fieldType}>
      {capitalize(fieldType)} Field
    </option>
  ))

  const project = useProject(projectId)

  let signatoryOptions: AutocompleteableItem[] = []

  if (project && project.signatories && project.signatories.length >= 1) {
    signatoryOptions = signatoryOptions.concat(
      project.signatories.map(signatory => ({
        id: signatory.id as number,
        value: `${signatory.firstName} ${signatory.lastName}`,
      })),
    )
  }

  const displayedValue = (param: keyof DefaultFields | 'userId') => {
    const updatedValue = inputs[param]
    const previousValue = annotation.data && annotation.data[param]
    const defaultValue = param === 'userId' ? '' : defaultFieldValues[param]

    if (updatedValue) {
      return updatedValue
    }
    if (previousValue) {
      return previousValue
    }
    return defaultValue
  }

  const selectInputText = (event: any): void => event.target.select()

  const selectSignatory: (item: AutocompleteableItem) => void = ({ id }) => {
    const foundSignatory = project?.signatories.find(
      signatory => signatory.id === id,
    )

    if (foundSignatory) {
      includeFormManagedValue({ userId: foundSignatory.id })
    } else {
      includeFormManagedValue({ userId: undefined })
    }
  }


  return (
    <div
      style={{
        background: 'white',
        left: `${annotation.geometry.x}%`,
        padding: '1rem',
        position: 'absolute',
        top: `${annotation.geometry.y + annotation.geometry.height}%`,
      }}>
      <Form noValidate validated={validated} onSubmit={submitHandler}>
        <Form.Group controlId="formAnnotationName">
          <Form.Label>Name</Form.Label>
          <Form.Control
            name="text"
            onChange={changeHandler}
            onFocus={selectInputText}
            placeholder={
              (annotation.data && annotation.data.text) || 'Name of Field'
            }
            required
            type="text"
            value={displayedValue('text')}
          />
        </Form.Group>
        <Form.Group controlId="formAnnotationType">
          <Form.Label>Input Type</Form.Label>
          <Form.Control
            as="select"
            name="inputType"
            onChange={changeHandler}
            required
            type="text"
            value={displayedValue('inputType')}>
            {fieldTypeOptions}
          </Form.Control>
        </Form.Group>

        {displayedValue('inputType') === "date" ? <Form.Group controlId="formAnnotationType">
          <Form.Check type="checkbox" label="Show auto filled date" name="isAutoFillDate" onChange={changeHandler} checked={annotation?.data?.isAutoFillDate} />
        </Form.Group> : null}

        <Form.Group>
          <Form.Label>Signatory User (Optional)</Form.Label>
          <SignatorySelect
            existingSignatoryId={annotation.data && annotation.data.userId}
            items={signatoryOptions}
            onSelect={selectSignatory}
          />
        </Form.Group>

        <Button variant="primary" value="Submit annotation" type="submit">
          Submit Annotation
        </Button>
      </Form>
      <Form onSubmit={deleteHandler}>
        {mode === 'update' && (
          <Button variant="primary" value="Delete annotation" type="submit">
            Delete Annotation
          </Button>
        )}
      </Form>
    </div>
  )
}
